import React from 'react'
import PropTypes from 'prop-types'
import { AI_RISK_REPORT_PDF_KEEP_CHUNK_WITH_NEXT_ATTRIBUTES } from '../../data/constants'
import { getSubsectionView } from '../../data/scoring'
import SectionHeading from '../../components/SectionHeading/SectionHeading'
import WhyThisMatters from '../../components/WhyThisMatters/WhyThisMatters'
import GroupGovernance from './GroupGovernance'
import GuestAccess from './GuestAccess'
import SensitivityDlp from './SensitivityDlp'
import SharePoint from './SharePoint'

export default function DataExposure({ summary = {}, data = {}, scoring = { sections: [] }, dataAvailability = [] }) {
  const sharePoint = getSubsectionView(scoring, 'sharePoint')
  const sensitivityDlp = getSubsectionView(scoring, 'sensitivityDlp')
  const groupGovernance = getSubsectionView(scoring, 'groupGovernance')
  const guestAccess = getSubsectionView(scoring, 'guestAccess')

  return (
    <>
      <div {...AI_RISK_REPORT_PDF_KEEP_CHUNK_WITH_NEXT_ATTRIBUTES}>
        <SectionHeading
          id="data-exposure"
          titleId="AI_RISK_REPORT.SECTION.DATA_EXPOSURE.TITLE"
          questionId="AI_RISK_REPORT.SECTION.DATA_EXPOSURE.QUESTION"
        />
        <WhyThisMatters bodyId="AI_RISK_REPORT.SECTION.DATA_EXPOSURE.WHY" />
      </div>
      <SharePoint
        tenantSettingAssessments={data.sharePointTenantSettingAssessments ?? []}
        sites={data.sharePointSites?.items ?? []}
        siteTotal={data.sharePointSites?.total ?? 0}
        oversharedSiteCount={data.sharePointSites?.oversharedTotal}
        applicationAccessSiteCount={data.sharePointSites?.applicationAccessTotal}
        score={sharePoint.score}
        gap={sharePoint.gap}
        partialGaps={sharePoint.partialGaps}
      />
      <SensitivityDlp
        summary={summary}
        sensitivityLabels={data.sensitivityLabels ?? []}
        dlpPolicies={data.dlpPolicies ?? []}
        score={sensitivityDlp.score}
        controls={sensitivityDlp.controls}
        gap={sensitivityDlp.gap}
        partialGaps={sensitivityDlp.partialGaps}
      />
      <GroupGovernance
        summary={summary}
        tenant={data.tenant}
        dynamicGroups={data.dynamicGroups ?? []}
        score={groupGovernance.score}
        gap={groupGovernance.gap}
        partialGaps={groupGovernance.partialGaps}
        dataAvailability={dataAvailability}
      />
      <GuestAccess summary={summary} guests={data.guestUsers ?? []} score={guestAccess.score} gap={guestAccess.gap} />
    </>
  )
}

const summaryPropType = PropTypes.shape({
  groups: PropTypes.shape({
    total: PropTypes.number,
    microsoft365: PropTypes.number,
    dynamic: PropTypes.number,
    dynamicWithMembershipRules: PropTypes.number,
    dynamicCanIncludeGuests: PropTypes.number,
  }),
  users: PropTypes.shape({
    total: PropTypes.number,
    guests: PropTypes.shape({ total: PropTypes.number, pending: PropTypes.number, accepted: PropTypes.number }),
  }),
})

DataExposure.propTypes = {
  summary: summaryPropType,
  data: PropTypes.shape({
    tenant: PropTypes.shape({}),
    sharePointTenantSettingAssessments: PropTypes.arrayOf(PropTypes.shape({})),
    sharePointSites: PropTypes.shape({
      total: PropTypes.number.isRequired,
      oversharedTotal: PropTypes.number,
      applicationAccessTotal: PropTypes.number,
      items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }),
    sensitivityLabels: PropTypes.arrayOf(PropTypes.shape({})),
    dlpPolicies: PropTypes.arrayOf(PropTypes.shape({})),
    dynamicGroups: PropTypes.arrayOf(PropTypes.shape({})),
    guestUsers: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  scoring: PropTypes.shape({ sections: PropTypes.arrayOf(PropTypes.shape({})) }),
  dataAvailability: PropTypes.arrayOf(PropTypes.shape({})),
}
